<script lang="ts">
  import SliderButton from '$components/ui/slider-button.svelte';
  import Spinner from '$components/ui/spinner.svelte';
  import type { GetCartQuery } from '$gql/storefront/operations';
  import { AddToCartLocations, elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  import { updateCartQuantity } from '$lib/cart';
  import { getProductCategory, lastAddedItemLocation } from '$lib/cart/cart';
  import { DEFAULT_VENDOR, GUEST } from '$lib/constants';
  import { lang } from '$lib/lang';
  import { region } from '$lib/region';
  import { getFullURL } from '$lib/utils/paths';
  import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';

  export let id: string;
  export let itemId: string;
  export let qty: number;
  export let product: NonNullable<GetCartQuery['cart']>['lines']['nodes'][number];
  let loading = false;

  const triggerElevar = (
    cartItem: NonNullable<GetCartQuery['cart']>['lines']['nodes'][number],
    shouldIncreaseQty?: boolean
  ) => {
    const variantId = trimVariantId(cartItem.merchandise.id);
    const collectionName =
      getProductCategory(
        cartItem.merchandise.product.title,
        cartItem.merchandise.product.productType
      ) || '';

    const payload = {
      actionField: {
        list: `/collections/${collectionName}`
      },
      products: [
        {
          id: cartItem.merchandise.sku,
          name: cartItem.merchandise.product.title,
          brand: cartItem.merchandise.product.vendor || DEFAULT_VENDOR,
          category: cartItem.merchandise.product.productType,
          variant: cartItem.merchandise.product.title,
          price: `${cartItem.cost.totalAmount.amount}`,
          list: `/collections/${collectionName}`,
          product_id: trimId(cartItem.merchandise.product.id),
          quantity: `${cartItem.quantity || 1}`,
          variant_id: variantId,
          compare_at_price: `${cartItem.merchandise.compareAtPrice?.amount}` || '0.0',
          image: cartItem.merchandise.image?.url,
          url: getFullURL({
            path: `/products/${cartItem.merchandise.product.handle}`,
            region: $region,
            lang: $lang,
            searchQuery: `variant=${variantId}`
          })
        }
      ]
    };

    if (shouldIncreaseQty) {
      elevarAnalyticsEvent({
        event: ElevarAnalyticsEvents.ADD_TO_CART,
        user_properties: {
          visitor_type: GUEST
        },
        ecommerce: {
          currencyCode: cartItem.cost.totalAmount.currencyCode,
          add: payload
        }
      });
    } else {
      elevarAnalyticsEvent({
        event: ElevarAnalyticsEvents.REMOVE_FROM_CART,
        user_properties: {
          visitor_type: GUEST
        },
        ecommerce: {
          currencyCode: cartItem.cost.totalAmount.currencyCode,
          remove: payload
        }
      });
    }
  };

  // debounced mutation
  let timer: NodeJS.Timeout;
  const updateCart = (quantity: number, shouldIncreaseQty?: boolean) => {
    qty = Math.max(0, quantity);

    clearTimeout(timer);
    timer = setTimeout(() => {
      loading = true;
      lastAddedItemLocation.set(AddToCartLocations.CART_QUANTITY_SELECTOR);

      if (product) {
        triggerElevar(product, shouldIncreaseQty);
      }

      $updateCartQuantity(id, qty, itemId).finally(() => {
        loading = false;
      });
    }, 500);
  };
</script>

<div class="flex items-center gap-2">
  <SliderButton
    icon={qty <= 1 ? 'trash' : 'minus-bold'}
    variant="ghost"
    on:click={() => updateCart(qty - 1)}
  />
  <span class="block text-sm font-semibold tabular-nums min-w-[20px] text-center">{qty}</span>
  <SliderButton icon="plus-bold" variant="ghost" on:click={() => updateCart(qty + 1, true)} />
  {#if loading}
    <Spinner size="xs" />
  {/if}
</div>
